import React, { useCallback, useRef, useState } from 'react';
import type { NumberLineItem } from '../../../../transformers/zod-schemas';
import type { UserAnswerValue } from '../../../types';
import {
  toPercent,
  pixelToValue,
  snapToNearest,
  cleanFloat,
  almostEqual,
} from '../../../utils/number-line.utils';
import { useJiggle } from '../../../dnd';
import { useSound } from '../../../sounds';

type CursorItem = NonNullable<NumberLineItem['cursors']>[number];
export type CursorValidationStatus = 'correct' | 'incorrect';
export type CursorValidationStatuses = Record<string, CursorValidationStatus>;

export interface CursorBallsProps {
  cursors: NumberLineItem['cursors'];
  min: number;
  max: number;
  snapTicks: number[];
  audibleTicks?: number[];
  axisRowRef: { current: HTMLDivElement | null };
  onChange: (cursorId: string, value: UserAnswerValue) => void;
  values: Record<string, UserAnswerValue>;
  cursorValidationStatuses?: CursorValidationStatuses;
}

// ─── Single draggable cursor ────────────────────────────────────────────────

interface DraggableCursorProps {
  cursor: CursorItem;
  min: number;
  max: number;
  snapTicks: number[];
  audibleTicks?: number[];
  axisRowRef: { current: HTMLDivElement | null };
  onChange: (cursorId: string, value: UserAnswerValue) => void;
  currentValue: number;
  validationStatus: CursorValidationStatus | null;
}

const DraggableCursor: React.FC<DraggableCursorProps> = ({
  cursor, min, max, snapTicks, audibleTicks, axisRowRef, onChange, currentValue, validationStatus,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const currentValueRef = useRef(currentValue);
  currentValueRef.current = currentValue;
  const dragStartValueRef = useRef(currentValue);
  const lastSoundValueRef = useRef(currentValue);
  const { jiggling, onAnimationEnd } = useJiggle(isDragging);
  const playSound = useSound();

  // Author color is dynamic; validation feedback colors live in CSS classes.
  const colorVar = !validationStatus && cursor.color
    ? ({ '--nl-cursor-color': cursor.color } as React.CSSProperties)
    : undefined;

  // Fill geometry: spans from the initial position (cursor.value) to the current ball
  const initialPct = toPercent(cursor.value, min, max);
  const ballPct    = toPercent(currentValue, min, max);
  const fillLeft   = Math.min(initialPct, ballPct);
  const fillWidth  = Math.abs(ballPct - initialPct);

  const snap = useCallback((clientX: number): number => {
    const el = axisRowRef.current;
    if (!el) return currentValueRef.current;
    const rect = el.getBoundingClientRect();
    const raw  = pixelToValue(clientX, rect.left, rect.width, min, max);
    return snapTicks.length > 0 ? snapToNearest(raw, snapTicks) : cleanFloat(raw);
  }, [axisRowRef, min, max, snapTicks]);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    e.stopPropagation();
    isDraggingRef.current = true;
    dragStartValueRef.current = currentValue;
    lastSoundValueRef.current = currentValue;
    setIsDragging(true);
  }, [currentValue]);

  const updateCursorValue = useCallback((clientX: number): number => {
    const next = snap(clientX);
    if (!almostEqual(next, lastSoundValueRef.current)) {
      const soundTicks = audibleTicks ?? snapTicks;
      if (soundTicks.some(tick => almostEqual(tick, next))) playSound('cursor-step');
      lastSoundValueRef.current = next;
    }
    onChange(cursor.id, String(next));
    return next;
  }, [snap, audibleTicks, snapTicks, playSound, onChange, cursor.id]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    e.stopPropagation();
    updateCursorValue(e.clientX);
  }, [updateCursorValue]);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    setIsDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
    e.stopPropagation();
    const next = updateCursorValue(e.clientX);
    if (!almostEqual(next, dragStartValueRef.current)) {
      playSound('cursor-release');
    }
  }, [updateCursorValue, playSound]);

  // Keyboard: left/right navigates to adjacent snap tick
  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!snapTicks.length) return;
    const sorted = [...snapTicks].sort((a, b) => a - b);
    const idx    = sorted.findIndex(t => t >= currentValue - 1e-9);
    const first  = sorted[0] ?? currentValue;
    const last   = sorted[sorted.length - 1] ?? currentValue;
    if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = sorted[idx + 1] ?? last;
      const soundTicks = audibleTicks ?? snapTicks;
      if (!almostEqual(next, currentValue) && soundTicks.some(tick => almostEqual(tick, next))) {
        playSound('cursor-step');
      }
      onChange(cursor.id, String(next));
    } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = sorted[idx - 1] ?? first;
      const soundTicks = audibleTicks ?? snapTicks;
      if (!almostEqual(next, currentValue) && soundTicks.some(tick => almostEqual(tick, next))) {
        playSound('cursor-step');
      }
      onChange(cursor.id, String(next));
    }
  }, [snapTicks, audibleTicks, currentValue, playSound, onChange, cursor.id]);

  const ballClass = [
    'number-line-cursor-ball',
    isDragging       ? 'number-line-cursor-ball--dragging'            : '',
    jiggling && !isDragging ? 'number-line-cursor-ball--jiggle'       : '',
    validationStatus ? `number-line-cursor-ball--${validationStatus}` : '',
  ].filter(Boolean).join(' ');
  const fillClass = [
    'number-line-cursor-fill',
    validationStatus ? `number-line-cursor-fill--${validationStatus}` : '',
  ].filter(Boolean).join(' ');
  const labelClass = [
    'number-line-cursor-label',
    validationStatus ? `number-line-cursor-label--${validationStatus}` : '',
  ].filter(Boolean).join(' ');
  const sliderAriaLabel = cursor.label
    ? `${cursor.label}: cursor position ${currentValue}`
    : `cursor position ${currentValue}`;

  return (
    <React.Fragment>
      {/* Fill: from initial position to current position, only when enabled and ball has moved */}
      {cursor.fillRange && fillWidth > 0 && (
        <div
          className={fillClass}
          aria-hidden="true"
          style={{ left: `${fillLeft}%`, width: `${fillWidth}%`, ...colorVar }}
        />
      )}

      {cursor.label && (
        <span
          className={labelClass}
          aria-hidden="true"
          style={{ left: `${ballPct}%`, ...colorVar }}
        >
          {cursor.label}
        </span>
      )}

      <div
        className={ballClass}
        style={{ left: `${ballPct}%`, ...colorVar }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onKeyDown={handleKeyDown}
        onAnimationEnd={onAnimationEnd}
        role="slider"
        aria-valuemin={min}
        aria-valuemax={max}
        aria-valuenow={currentValue}
        aria-label={sliderAriaLabel}
        tabIndex={0}
      />
    </React.Fragment>
  );
};

// ─── Public component ────────────────────────────────────────────────────────

export const CursorBalls: React.FC<CursorBallsProps> = ({
  cursors, min, max, snapTicks, audibleTicks, axisRowRef, onChange, values, cursorValidationStatuses,
}) => {
  if (!cursors?.length) return null;

  return (
    <>
      {cursors.map(cursor => {
        const raw          = values[cursor.id];
        const parsed = Number(raw);
        const currentValue = raw !== undefined && raw !== '' && isFinite(parsed) ? parsed : cursor.value;
        const validationStatus = cursorValidationStatuses?.[cursor.id] ?? null;

        return (
          <DraggableCursor
            key={cursor.id}
            cursor={cursor}
            min={min}
            max={max}
            snapTicks={snapTicks}
            audibleTicks={audibleTicks}
            axisRowRef={axisRowRef}
            onChange={onChange}
            currentValue={currentValue}
            validationStatus={validationStatus}
          />
        );
      })}
    </>
  );
};
